<template>
  <div class="content-wrapper view-aptlist-manage p-0">
    <div class="content">
      <div class="card">
        <div class="card-body">
          <!-- Header -->
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div class="d-flex align-items-center gap-2">
              <b-button
                variant="outline"
                size="sm"
                @click="handleBack"
                v-b-tooltip.hover
                title="ย้อนกลับ"
              >
                <i
                  class="fas fa-chevron-left"
                  style="font-size: 20px"
                ></i>
              </b-button>
              <h4 class="mb-0">ใบรับสินค้า</h4>
              <GrStatusBadge
                v-if="data"
                :status="data.status"
                :syncDt="data.syncDt"
              />
            </div>
            <div
              v-if="isEditable"
              class="d-flex gap-2"
            >
              <b-button
                v-if="
                  isAccessMenu([
                    ROLES.ROLE_BRANCH_STOCK_MANAGER,
                    ROLES.ROLE_STOCK_STAFF,
                  ])
                "
                class="outline-btn"
                size="sm"
                @click="openEditModal"
              >
                แก้ไขรายการ
              </b-button>
              <b-button
                v-if="
                  isAccessMenu([
                    ROLES.ROLE_STOCK_STAFF,
                    ROLES.ROLE_BRANCH_STOCK_MANAGER,
                  ])
                "
                class="outline-danger-btn"
                size="sm"
                @click="deleteGoodsReceive"
              >
                ยกเลิกรายการ
              </b-button>
              <b-button
                v-if="canApprove"
                class="approve-btn mb-2 mb-md-0 d-flex align-items-center justify-content-center"
                variant="primary"
                size="sm"
                @click="showApproveConfirm = true"
              >
                อนุมัติ
              </b-button>
            </div>
          </div>

          <!-- Two-column layout -->
          <div
            v-if="!isLoading && data"
            class="gr-detail-layout"
          >
            <!-- Left: metadata -->
            <div class="gr-detail-info">
              <div class="info-row">
                <span class="info-label">เลขที่เอกสาร:</span>
                <span class="info-value">{{ data.grCode || "-" }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">รหัสใบสั่งซื้ออ้างอิง:</span>
                <span class="info-value">{{ data.poRefCode || "-" }}</span>
              </div>
              <div
                v-if="data.files && data.files.length > 0"
                class="info-row gr-files-row"
              >
                <span class="info-label">ไฟล์ใบรับสินค้า:</span>
                <div class="gr-files-list">
                  <GrFileChip
                    v-for="(file, index) in data.files"
                    :key="file.id || index"
                    :filename="file.originalName"
                    :show-delete="false"
                    @click="previewFile(file.id)"
                  />
                </div>
              </div>
              <div class="info-row">
                <span class="info-label">รหัสผู้ขาย:</span>
                <span class="info-value">{{ data.vendorCode || "-" }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">ชื่อผู้ขาย:</span>
                <span class="info-value">{{ data.vendorName || "-" }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">ชื่อสาขา:</span>
                <span class="info-value">{{ data.branchName || "-" }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">ต้องได้รับการอนุมัติจาก:</span>
                <span class="info-value">
                  <ApprovalSteps :show-hq="data.needHqApproval" />
                </span>
              </div>
              <div class="info-row">
                <span class="info-label">วันที่รับของ:</span>
                <span class="info-value">{{
                  data.receivedDt ? formatDate(data.receivedDt) : "-"
                }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">วันที่ทำรายการ:</span>
                <span class="info-value">{{ formatDate(data.createdAt) }}</span>
              </div>
              <div
                v-if="showRejectReason"
                class="reject-reason-box"
              >
                <div class="reject-reason-title">เหตุผลที่ถูกปฏิเสธ:</div>
                <div>{{ data.rejectReason }}</div>
              </div>
            </div>

            <!-- Right: items table -->
            <div class="gr-detail-table">
              <b-table
                id="grDetailPageTable"
                hover
                small
                responsive
                head-variant="light"
                :items="sortedItems"
                :fields="fields"
              >
                <template v-slot:cell(index)="row"
                  >{{ row.index + 1 }}.</template
                >
                <template v-slot:cell(image)="row">
                  <ProductImage :src="row.item.imageUrl || null" />
                </template>
                <template v-slot:cell(productCode)="row">
                  {{ row.item.productCode || row.item.product?.no || "-" }}
                </template>
                <template v-slot:cell(productName)="row">
                  <span
                    v-if="row.item.productType === 'FREE'"
                    class="free-label"
                    >[ของแถม]</span
                  >{{
                    row.item.productNameMain ||
                    row.item.product?.description ||
                    "-"
                  }}
                </template>
                <template v-slot:cell(quantity)="row">
                  {{ Number(row.item.quantity).toLocaleString() }}
                </template>
                <template v-slot:cell(uomName)="row">
                  {{ row.item.uomName || row.item.uom?.description || "-" }}
                </template>
                <template v-slot:cell(lotNumber)="row">
                  {{ row.item.lotNumber || "-" }}
                </template>
                <template v-slot:cell(expiredDt)="row">
                  {{
                    row.item.expiredDt ? formatDate(row.item.expiredDt) : "-"
                  }}
                </template>
              </b-table>
            </div>
          </div>

          <!-- Loading state -->
          <div
            v-if="isLoading"
            class="text-center py-5"
          >
            <b-spinner variant="primary"></b-spinner>
          </div>
        </div>
      </div>

      <GoodsReceiveSelectPOModal
        ref="selectPOModal"
        @updated="onGrUpdated"
        @error="onGrError"
      ></GoodsReceiveSelectPOModal>
      <Dialog ref="Dialog"></Dialog>

      <b-modal
        v-model="showApproveConfirm"
        title="อนุมัติใบรับสินค้า"
        centered
        no-close-on-backdrop
      >
        <p>
          {{ approveConfirmText }}<br />คุณต้องการอนุมัติใบรับสินค้านี้ใช่หรือไม่?
        </p>
        <template v-slot:modal-footer>
          <b-button
            class="cancel-btn"
            variant="outline-secondary"
            @click="showApproveConfirm = false"
            >ย้อนกลับ</b-button
          >
          <b-button
            class="confirm-btn"
            variant="primary"
            @click="approveGoodsReceive"
            >ยืนยันอนุมัติ</b-button
          >
        </template>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { THAI_MONTHS } from "@/constants";
import ProductImage from "@/components/table/stock/component/ProductImage.vue";
import GrFileChip from "@/components/modal/ProductAndStock/GrFileChip.vue";
import GoodsReceiveSelectPOModal from "@/components/modal/ProductAndStock/GoodsReceiveSelectPOModal.vue";
import Dialog from "@/components/modal/Dialog.vue";
import GrStatusBadge from "@/components/badge/GrStatusBadge.vue";
import ApprovalSteps from "@/components/badge/ProductAndStock/ApprovalSteps.vue";
import { GR_STATUS } from "@/constants/productStock";
import { ROLES } from "@/constants/user";

export default {
  name: "GoodsReceiveDetailPage",
  components: {
    ProductImage,
    GrFileChip,
    GoodsReceiveSelectPOModal,
    Dialog,
    GrStatusBadge,
    ApprovalSteps,
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fromRoute = from;
    });
  },
  data() {
    return {
      isLoading: false,
      fromRoute: null,
      data: null,
      showApproveConfirm: false,
      ROLES,
      GR_STATUS,
      fields: [
        {
          key: "index",
          label: "",
          thStyle: { width: "40px", minWidth: "40px" },
          class: "text-center",
        },
        {
          key: "image",
          label: "",
          thStyle: { width: "50px", minWidth: "50px" },
        },
        {
          key: "productCode",
          label: "รหัส",
          thStyle: { minWidth: "100px" },
        },
        {
          key: "productName",
          label: "ชื่อสินค้า",
          thStyle: { minWidth: "160px" },
          tdClass: "product-name-cell",
        },
        {
          key: "quantity",
          label: "จำนวนสินค้า",
          thStyle: { minWidth: "100px" },
          class: "text-right",
        },
        {
          key: "uomName",
          label: "หน่วยสินค้า",
          thStyle: { minWidth: "100px" },
        },
        {
          key: "lotNumber",
          label: "เลขล็อต",
          thStyle: { minWidth: "100px" },
          class: "text-right",
        },
        {
          key: "expiredDt",
          label: "วันหมดอายุ",
          thStyle: { minWidth: "120px" },
          class: "text-right",
        },
      ],
    };
  },
  async mounted() {
    await this.loadData();
  },
  computed: {
    ...mapGetters({
      productList: "moduleProductInventory/getProductList",
      isAccessMenu: "moduleUser/getIsAccessMenu",
    }),
    sortedItems() {
      const items = (this.data && this.data.items) || [];
      const withImage = items.map(item => ({
        ...item,
        imageUrl:
          this.productList.find(p => p.productId === item.productId)
            ?.imageUrl || null,
      }));
      return [
        ...withImage.filter(i => i.productType !== "FREE"),
        ...withImage.filter(i => i.productType === "FREE"),
      ];
    },
    isEditable() {
      return (
        this.data &&
        this.data.syncDt === null &&
        (this.data.status === GR_STATUS.WAITING_BM ||
          this.data.status === GR_STATUS.REJECTED)
      );
    },
    canApprove() {
      return (
        this.isAccessMenu([ROLES.ROLE_BRANCH_STOCK_MANAGER]) &&
        this.data?.status === GR_STATUS.WAITING_BM
      );
    },
    showRejectReason() {
      return this.data?.status === GR_STATUS.REJECTED && !!this.data?.rejectReason;
    },
    approveConfirmText() {
      return this.data?.needHqApproval
        ? "เมื่ออนุมัติแล้ว ใบรับสินค้าจะไม่สามารถแก้ไขได้ และรายการจะถูกส่งต่อให้สำนักงานใหญ่อนุมัติ"
        : "เมื่ออนุมัติแล้ว ใบรับสินค้าจะไม่สามารถแก้ไขได้ และสต๊อกจะถูกอัปเดต";
    },
  },
  methods: {
    ...mapActions({
      fetchGoodsReceiveById: "moduleGoodsReceive/fetchGoodsReceiveById",
      fetchProductList: "moduleProductInventory/fetchProductList",
      previewMediaFile: "moduleGoodsReceive/previewMediaFile",
      deleteGoodsReceiveAction: "moduleGoodsReceive/deleteGoodsReceive",
      approveGoodsReceiveAction: "moduleGoodsReceive/approveGoodsReceive",
    }),
    async loadData() {
      this.isLoading = true;
      try {
        const { clinicUrl, branchUrl, grId } = this.$route.params;

        // Ensure product list is available for images
        await this.fetchProductList({ clinicUrl, branchUrl });

        const detail = await this.fetchGoodsReceiveById({
          clinicUrl,
          branchUrl,
          id: grId,
        });

        if (!detail) {
          this.$refs.Dialog.showToast(
            "danger",
            "รายการนี้ถูกลบไปแล้ว กรุณารีเฟรชหน้าเพื่ออัปเดตข้อมูล"
          );
          setTimeout(() => {
            this.goBack();
          }, 1500);
          return;
        }

        this.data = detail;
      } catch (err) {
        console.error("fetchGoodsReceiveById failed", err);
        const errorMessage = err.response?.data?.errorMessage;
        const errorMsg =
          typeof errorMessage === "string"
            ? errorMessage
            : "โหลดข้อมูลไม่สำเร็จ กรุณาลองใหม่";
        this.$refs.Dialog.showToast("danger", errorMsg);
        this.goBack();
      } finally {
        this.isLoading = false;
      }
    },
    openEditModal() {
      this.$refs.selectPOModal.show({ goodsReceive: this.data });
    },
    approveGoodsReceive() {
      this.showApproveConfirm = false;
      this.isLoading = true;

      this.approveGoodsReceiveAction({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        id: this.data.id,
      })
        .then(res => {
          if (res.status === 200) {
            this.$refs.Dialog.showToast(
              "success",
              "อนุมัติใบรับสินค้าสำเร็จ อัปเดตสต๊อกแล้ว"
            );
            this.loadData();
          }
        })
        .catch(err => {
          const errorMessage = err.response?.data?.errorMessage;
          const errorMsg =
            typeof errorMessage === "string"
              ? errorMessage
              : "อนุมัติใบรับสินค้าไม่สำเร็จ กรุณาลองใหม่";
          this.$refs.Dialog.showToast("danger", errorMsg);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    deleteGoodsReceive() {
      this.$refs.Dialog.showAlertConfirm(
        "ยกเลิกใบรับสินค้านี้ ?",
        "คุณต้องการยกเลิกใบรับสินค้านี้ใช่หรือไม่"
      ).then(result => {
        if (!result.value) return;
        this.isLoading = true;

        this.deleteGoodsReceiveAction({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          id: this.data.id,
        })
          .then(res => {
            if (res.status === 200) {
              this.$refs.Dialog.showToast(
                "success",
                "ยกเลิกรายการใบรับสินค้าสำเร็จ"
              );
              setTimeout(() => {
                this.goBack();
              }, 1500);
            }
          })
          .catch(err => {
            const errorMessage = err.response?.data?.errorMessage;
            const errorMsg =
              typeof errorMessage === "string"
                ? errorMessage
                : "ยกเลิกใบรับสินค้าไม่สำเร็จ กรุณาลองใหม่";
            this.$refs.Dialog.showToast("danger", errorMsg);
          })
          .finally(() => {
            this.isLoading = false;
          });
      });
    },
    onGrUpdated() {
      this.$refs.Dialog.showToast("success", "แก้ไขรายการใบรับสินค้าสำเร็จ");
      this.loadData();
    },
    onGrError(message) {
      this.$refs.Dialog.showToast("danger", message);
    },
    handleBack() {
      if (this.fromRoute?.name) {
        this.$router.go(-1);
      } else {
        this.goBack();
      }
    },
    goBack() {
      this.$router.push({
        name: "GoodsReceive",
        params: {
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
        },
      });
    },
    async previewFile(id) {
      const result = await this.previewMediaFile({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        id,
      });
      if (result.success) return;
      if (result.reason === "not_found") {
        this.$refs.Dialog.showToast("danger", "ไม่พบไฟล์");
      } else if (result.reason === "error") {
        this.$refs.Dialog.showToast("danger", "เปิดไฟล์ไม่สำเร็จ");
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const day = date.getDate();
      const month = THAI_MONTHS[date.getMonth()];
      const year = String(date.getFullYear()).slice(-2);
      return `${day} ${month} ${year}`;
    },
    formatNumber(val) {
      if (val === null || val === undefined || val === "") return "-";
      return Number(val).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
h4 {
  margin: 0;
}

.gap-2 {
  gap: 8px;
}

.gr-detail-layout {
  display: flex;
  gap: 24px;
  border: 1px solid #dee2e6;
  padding: 20px;
}

.gr-detail-info {
  flex: 0 0 30%;
  min-width: 0;
  border-right: 1px solid #dee2e6;
  padding-right: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.gr-detail-table {
  flex: 1;
  min-width: 0;
  overflow-x: auto;
}

.info-row {
  line-height: 1.6;
  display: flex;
}

.info-label {
  font-weight: 600;
  color: #000;
  white-space: nowrap;
  flex-shrink: 0;
}

.info-value {
  margin-left: 4px;
  word-break: break-word;
  min-width: 0;
  flex: 1;
}

.free-label {
  margin-right: 4px;
  color: #212529;
}

.reject-reason-box {
  background: #FFD9DD;
  border-radius: 4px;
  padding: 16px;
}

.reject-reason-title {
  font-weight: 600;
  font-size: 16px;
  color: #DD3333;
  margin-bottom: 4px;
}

.gr-files-row {
  flex-direction: column;
  gap: 8px;
}

.gr-files-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

::v-deep #grDetailPageTable thead th,
::v-deep #grDetailPageTable tbody td {
  vertical-align: middle !important;
}

::v-deep #grDetailPageTable thead th {
  font-size: 0.85rem;
  white-space: nowrap;
}

::v-deep #grDetailPageTable tbody td {
  font-size: 0.9rem;
  word-break: break-word;
  white-space: normal;
}

::v-deep #grDetailPageTable tbody tr:last-child td {
  border-bottom: 1px solid #dee2e6 !important;
}

.outline-btn {
  padding: 8px 30px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #5299d6;
  color: #5299d6;
  background: transparent;
  transition: all 0.2s ease;
}

.outline-btn:hover {
  background-color: #5299d6;
  color: #fff;
}

.outline-danger-btn {
  padding: 8px 30px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid #dc3545;
  color: #dc3545;
  background: transparent;
  transition: all 0.2s ease;
}

.outline-danger-btn:hover {
  background-color: #dc3545;
  color: #fff;
}

.approve-btn {
  padding: 8px 50px;
  font-size: 14px;
  font-weight: 600;
}

.cancel-btn {
  padding: 8px 30px;
  font-size: 14px;
  font-weight: 600;
  border-color: #acb5bd;
  color: #212529;
}
.cancel-btn:hover {
  background-color: #acb5bd;
  color: #fff;
}

.confirm-btn {
  padding: 8px 50px;
  font-size: 14px;
  font-weight: 600;
}

@media (max-width: 768px) {
  .gr-detail-layout {
    flex-direction: column;
  }

  .gr-detail-info {
    flex: none;
    border-right: none;
    padding-right: 0;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 16px;
  }
}
</style>
